import type {PointerEvent, ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import React from 'react';
import type {CustomLineItemControlsWrapperProps} from '@Components/poster-editor/components/custom-line-item-controls-wrapper/custom-line-item-controls-wrapper.types';
import {useSingleActivePosterItem} from '@Hooks/poster-editor/useSingleActivePosterItem';
import {useAppSelector} from '@/hooks';
import type {FabricObject} from '@postermywall/fabricjs-2';
import {util} from '@postermywall/fabricjs-2';
import {
  CUSTOM_CONTROLS_WRAPPER,
  CUSTOM_LINE_ITEM_DIMENSIONS_CONTROL,
  CustomControlCursor,
} from '@Components/poster-editor/components/custom-item-controls/custom-item-controls.types';
import {isEditorMobileVariant} from '@Components/poster-editor/library/poster-editor-library';
import {degreesToRadians, radiansToDegrees} from '@Utils/math.util';
import {getDeltaWidthForMiddleControl} from '@PosterWhiteboard/libraries/custom-item-controls.library';
import type {CustomItemControlPositions} from '@Components/poster-editor/components/custom-item-controls/components/single-item-custom-controls/single-item-custom-controls.types';
import type {Page} from '@PosterWhiteboard/page/page.class';
import {CUSTOM_LINE_ITEM_MIDDLE_LEFT_CONTROL, MAX_SNAPPING_THRESHOLD, TUNABLE_THRESHOLD_CONSTANT} from '@Components/poster-editor/components/custom-line-item-controls-wrapper/components/custom-line-item-control-ml/custom-line-item-control-ml.types';
import {BaseCustomItemControl} from '@Components/poster-editor/components/custom-item-controls/components/base-custom-item-control/base-custom-item-control';

export function CustomLineItemControlMl({...props}: CustomLineItemControlsWrapperProps): ReactElement | null {
  const activeItem = useSingleActivePosterItem(props.pageHashedId);

  const arePosterItemsScaling = useAppSelector((state) => {
    return state.posterEditor.arePosterItemsScaling[props.pageHashedId];
  });
  const arePosterItemsMoving = useAppSelector((state) => {
    return state.posterEditor.arePosterItemsMoving[props.pageHashedId];
  });
  const verticalScroll = useAppSelector((state) => {
    return state.posterEditor.scrollState.verticalScroll;
  });
  const horizontalScroll = useAppSelector((state) => {
    return state.posterEditor.scrollState.horizontalScroll;
  });
  const [isActive, setIsActive] = useState(false);
  const initialClientX = useRef<number | null>(null);
  const initialClientY = useRef<number | null>(null);

  useEffect(() => {
    if (isActive) {
      window.addEventListener('pointerup', handlePointerUp);
      window.addEventListener('pointermove', handlePointerMove);
    }

    return (): void => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [isActive]);

  if (!activeItem) {
    return null;
  }
  const getHorizontalCorrection = (fabricObject: FabricObject): number => {
    const controlsWrapperSelector = `.${CUSTOM_CONTROLS_WRAPPER}#${CSS.escape(props.pageHashedId)}`;
    const controlsWrapper = document.querySelector<HTMLElement>(controlsWrapperSelector);
    if (isEditorMobileVariant() || !controlsWrapper) {
      return horizontalScroll + (fabricObject.canvas?._offset.left ?? 0);
    }

    const wrapperRect = controlsWrapper.getBoundingClientRect();

    return wrapperRect.left;
  };

  const getVerticalCorrection = (fabricObject: FabricObject): number => {
    const controlsWrapperSelector = `.${CUSTOM_CONTROLS_WRAPPER}#${CSS.escape(props.pageHashedId)}`;
    const controlsWrapper = document.querySelector<HTMLElement>(controlsWrapperSelector);

    if (isEditorMobileVariant() || !controlsWrapper) {
      return verticalScroll + (fabricObject.canvas?._offset.top ?? 0);
    }

    const wrapperRect = controlsWrapper.getBoundingClientRect();

    return wrapperRect.top;
  };

  const handlePointerMove = (e: globalThis.PointerEvent): void => {
    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page) {
      return;
    }

    if (!isActive) {
      return;
    }

    if (initialClientX.current === null) {
      return;
    }

    onRotateSelection(e);
    onItemResizedWithLeftHandle(e);
  };

  const handlePointerUp = (): void => {
    if (!activeItem) {
      return;
    }

    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page) {
      return;
    }

    if (!isActive) {
      return;
    }

    setIsActive(false);

    page.setPosterItemsModificationState(false);

    void onLineModificationEnded();

    initialClientX.current = null;
    initialClientY.current = null;
  };

  const onLineModificationEnded = async (): Promise<void> => {
    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page) {
      return;
    }

    if (!activeItem) {
      return;
    }

    await activeItem.updateFromObject(
      {
        angle: activeItem.fabricObject.angle,
        left: activeItem.fabricObject.getX(),
        top: activeItem.fabricObject.getY(),
        scaleX: activeItem.fabricObject.getObjectScaling().x,
        scaleY: activeItem.fabricObject.getObjectScaling().y,
      },
      {undoable: false}
    );

    page.poster.history.addPosterHistory();
  };

  const handlePointerDown  = (e: PointerEvent): void => {
    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page) {
      return;
    }

    const target = e.target as HTMLElement;
    if (target.id === CUSTOM_LINE_ITEM_MIDDLE_LEFT_CONTROL) {
      setIsActive(true);

      initialClientX.current = e.clientX;
      initialClientY.current = e.clientY;

      page.setPosterItemsModificationState('scaling');
    }
  };

  const defaultOnItemLeftHandleDragged = (e: globalThis.PointerEvent, deltaW: number): void => {
    const originalScaledWidth = activeItem.getScaledWidth();
    const changedScaledWidth = originalScaledWidth - deltaW;
    if (changedScaledWidth <= 0) {
      return;
    }

    const newScaleX = changedScaledWidth / activeItem.fabricObject.width;

    activeItem.fabricObject.set({
      scaleX: newScaleX,
      left: activeItem.getX() + deltaW * Math.cos(degreesToRadians(activeItem.fabricObject.angle)),
      top: activeItem.getY() + deltaW * Math.sin(degreesToRadians(activeItem.fabricObject.angle)),
    });

    activeItem.onScaling();

    updateInitialRefs(e);
    refreshFabricObject();
  };

  const refreshFabricObject = (): void => {
    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page) {
      return;
    }

    activeItem.fabricObject.setCoords();

    page.fabricCanvas.requestRenderAll();
  };

  const onItemResizedWithLeftHandle = (e: globalThis.PointerEvent): void => {
    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page) {
      return;
    }
    const activeSelection = page.activeSelection.getActiveObject();
    if (!activeSelection) {
      return;
    }

    if (initialClientX.current === null || initialClientY.current === null) {
      return;
    }

    const angleRadians = degreesToRadians(activeItem.fabricObject.angle);

    const currentCursorCanvasX = e.clientX - getHorizontalCorrection(activeSelection)
    const currentCursorCanvasY = e.clientY - getVerticalCorrection(activeSelection);

    const fabricObjectDefaultRotationX = activeSelection.getX() * page.poster.scaling.scale;
    const fabricObjectDefaultRotationY = (activeSelection.getY() + activeSelection.getScaledHeight() / 2) * page.poster.scaling.scale;

    const deltaW = getDeltaWidthForMiddleControl(currentCursorCanvasX, fabricObjectDefaultRotationX, currentCursorCanvasY, fabricObjectDefaultRotationY, page.poster.scaling.scale, angleRadians);

    defaultOnItemLeftHandleDragged(e, deltaW);
  };

  const getMlControlPosition = (): CustomItemControlPositions => {
    const itemHeight = activeItem.fabricObject.getScaledHeight() * activeItem.page.poster.scaling.scale;

    const leftInitial = activeItem.fabricObject.getX() * activeItem.page.poster.scaling.scale;
    const topInitial = activeItem.fabricObject.getY() * activeItem.page.poster.scaling.scale;

    const xCorrectionDueToItemHeight = (itemHeight / 2) * Math.sin(degreesToRadians(activeItem.fabricObject.getTotalAngle()));

    const yCorrectionDueToItemHeight = (itemHeight / 2) * Math.cos(degreesToRadians(activeItem.fabricObject.getTotalAngle()));

    const angle = activeItem.fabricObject.getTotalAngle();
    const left = leftInitial - xCorrectionDueToItemHeight - Math.ceil(CUSTOM_LINE_ITEM_DIMENSIONS_CONTROL / 2);
    const top = topInitial + yCorrectionDueToItemHeight - Math.ceil(CUSTOM_LINE_ITEM_DIMENSIONS_CONTROL / 2);

    return {left, top, angle};
  };

  const getNewRotationAngle = (angleBetweenFabricObjectCenterAndCursor: number): number | undefined => {
    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page) {
      return;
    }
    const activeSelection = page.activeSelection.getActiveObject();
    if (!activeSelection) {
      return;
    }

    let angle = angleBetweenFabricObjectCenterAndCursor;

    const lineWidth = activeSelection.getScaledWidth();

    const snappingThreshold = Math.min(MAX_SNAPPING_THRESHOLD , TUNABLE_THRESHOLD_CONSTANT / lineWidth);

    if (angleBetweenFabricObjectCenterAndCursor < 0) {
      angle += 360;
    }

    const targetAngles = [0, 45, 90, 135, 180, 225, 270, 315, 360];

    for (const targetAngle of targetAngles) {
      if (Math.abs(angle - targetAngle) <= snappingThreshold) {
        return targetAngle;
      }
    }

    return angle;
  };

  const getAngleBetweenFabricObjectRotationPivotAndCursor = (
    e: globalThis.PointerEvent,
    fabricObject: FabricObject,
    currentPage: Page,
    horizontalAdjustment: number,
    verticalAdjustment: number
  ): number => {
    const currentCursorCanvasX = e.clientX - horizontalAdjustment;
    const currentCursorCanvasY = e.clientY - verticalAdjustment;

    const fabricObjectRotationPivotX = (fabricObject.getX() + fabricObject.getScaledWidth()) * currentPage.poster.scaling.scale;
    const fabricObjectRotationPivotY = (fabricObject.getY() + fabricObject.getScaledHeight()/2) * currentPage.poster.scaling.scale;

    let angle = -1 * radiansToDegrees(Math.atan2(fabricObjectRotationPivotX - currentCursorCanvasX, fabricObjectRotationPivotY - currentCursorCanvasY)) - 90;
    angle  = 180 + angle;
    return  angle;
  };


  const getOffsetsForMLControlBasedOnAngle = (angle: number): { xOffset: number; yOffset: number } | undefined => {
    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page) {
      return;
    }

    const activeSelection = page.activeSelection.getActiveObject();
    if (!activeSelection) {
      return;
    }

    const width = activeSelection.getScaledWidth();
    const h = activeSelection.getScaledHeight() / 2;
    const rad = util.degreesToRadians(angle);
    const xOffset = (width - (width * Math.cos(rad))) + (h * Math.sin(rad));
    const yOffset = (-width * Math.sin(rad)) + (h - (h * Math.cos(rad)));

    return { xOffset, yOffset };
  };

  const onRotateSelection = (e: globalThis.PointerEvent): void => {
    if (initialClientX.current === null || initialClientY.current === null) {
      return;
    }

    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page) {
      return;
    }

    const activeSelection = page.activeSelection.getActiveObject();
    if (!activeSelection) {
      return;
    }

    const currentAngle = activeSelection.angle;
    const initialOffsets = getOffsetsForMLControlBasedOnAngle(currentAngle);
    if(!initialOffsets){
      return;
    }
    activeSelection.set({angle: 0, left: activeSelection.getX() - initialOffsets.xOffset, top: activeSelection.getY() - initialOffsets.yOffset});
    activeSelection.setCoords();

    const angleBetweenFabricObjectCenterAndCursor = getAngleBetweenFabricObjectRotationPivotAndCursor(
      e,
      activeSelection,
      page,
      getHorizontalCorrection(activeSelection),
      getVerticalCorrection(activeSelection)
    );
    const newRotationAngle = getNewRotationAngle(angleBetweenFabricObjectCenterAndCursor);
    if(!newRotationAngle){
      return;
    }
    const newOffsets = getOffsetsForMLControlBasedOnAngle(newRotationAngle)
    if (!newOffsets){
      return;
    }

    activeSelection.set({angle: newRotationAngle, left: activeSelection.getX() + newOffsets.xOffset, top: activeSelection.getY() + newOffsets.yOffset});

    if (angleBetweenFabricObjectCenterAndCursor !== newRotationAngle) {
      updateInitialRefs(e);
    }

    page.fabricCanvas.requestRenderAll();
  };

  if (arePosterItemsMoving || (arePosterItemsScaling && !isActive)) {
    return null;
  }

  const updateInitialRefs = (e: globalThis.PointerEvent): void => {
    initialClientX.current = e.clientX;
    initialClientY.current = e.clientY;
  };

  return (
    <BaseCustomItemControl
      controlId={CUSTOM_LINE_ITEM_MIDDLE_LEFT_CONTROL}
      top={getMlControlPosition().top}
      left={getMlControlPosition().left}
      width={CUSTOM_LINE_ITEM_DIMENSIONS_CONTROL}
      height={CUSTOM_LINE_ITEM_DIMENSIONS_CONTROL}
      angle={getMlControlPosition().angle}
      isVisible
      cursor={CustomControlCursor.CURSOR_CROSSHAIR}
      onPointerDown={handlePointerDown}
    />
  );
}
